/**
 * Danh sách quản trị cụm loa / bảng điện tử — "Quản lý kho thiết bị kết nối".
 *
 * Luồng đăng ký thiết bị do nhà cung cấp gửi DV008 lên (migration 036):
 *   1. "Thêm mới" mở hộp "Danh sách thiết bị kết nối" (thiết bị phát hiện qua DV008)
 *      → tích chọn → "Đăng ký" đưa vào KHO chờ đăng ký;
 *   2. tab "Kho chờ đăng ký": chọn địa bàn + thiết bị → "Đăng ký thiết bị (đã chọn)"
 *      hoặc "Đăng ký tất cả thiết bị" → tạo thiết bị trong hệ thống.
 * Tab "Đã đăng ký vào hệ thống" giữ bảng thiết bị cũ (xoá, chỉnh sửa).
 * Mọi danh sách lọc, đếm và phân trang ở phía máy chủ.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viThietBi } from '../ngon-ngu/vi-thiet-bi';
import { thietBiApi, type DongThietBi, type NutDiaBan, type TrangDuLieu } from '../api/thiet-bi-api';
import {
  khoThietBiApi,
  LoiApi,
  type DongKho,
  type KetQuaDangKy,
  type KhoThietBiApi,
  type LoaiThietBiKho,
} from '../api/kho-thiet-bi-api';
import { dungHoan, dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { NhanChuaCoApi, TruongLoc } from '../thanh-phan/MauGiaoDien';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { gioThanhChu } from '../thanh-phan/NhanTrangThai';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { ChonDonViHanhChinh, TRE_TIM_KIEM_MS } from './MH02DanhSachThietBi';
import { HopThietBiKetNoi, ODiaBan, OPhuongThuc, laChuaCoXa, thoiDiemNgan } from './HopThietBiKetNoi';
import './thiet-bi.css';

export interface QuanTriThietBiProps {
  loai: LoaiThietBiKho;
  /** Ghi đè lớp API, dùng cho kiểm thử thành phần. */
  api?: Pick<typeof thietBiApi, 'danhSach' | 'xoa'> &
    Partial<Pick<typeof thietBiApi, 'cayDiaBan' | 'nhaCungCap'>>;
  apiKho?: KhoThietBiApi;
}

/** Biểu tượng nét nhỏ cho hàng nút hàng loạt (vẽ tại chỗ, cùng khuôn BieuTuong). */
function BieuTuongNho({ duong }: { duong: string }): ReactNode {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={duong} />
    </svg>
  );
}

const NET_DONG_BO = 'M4 12a8 8 0 0 1 14-5.3L20 9M20 4v5h-5M20 12a8 8 0 0 1-14 5.3L4 15M4 20v-5h5';
const NET_TEP = 'M14 3H6v18h12V7l-4-4zM14 3v4h4M9 13h6M9 17h6';
const NET_THUNG_RAC = 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13';
const NET_CONG = 'M12 5v14M5 12h14';

type ThongBao = { loai: 'ok' | 'loi'; chu: string; chiTiet?: string[] } | null;

const thongDiepLoi = (e: unknown): string =>
  e instanceof LoiApi || e instanceof Error ? e.message : String(e);

function DongThongBao({ tb }: { tb: ThongBao }): ReactNode {
  if (!tb) return null;
  return (
    <div className="qt-thong-bao" role={tb.loai === 'loi' ? 'alert' : 'status'}>
      <p>{tb.chu}</p>
      {tb.chiTiet && tb.chiTiet.length > 0 ? (
        <ul>
          {tb.chiTiet.slice(0, 20).map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ============================== Kho chờ đăng ký ============================= */

function HopSuaKho({
  dong,
  cay,
  api,
  onXong,
  onHuy,
}: {
  dong: DongKho;
  cay: NutDiaBan[];
  api: KhoThietBiApi;
  onXong: (d: DongKho) => void;
  onHuy: () => void;
}): ReactNode {
  const t = viThietBi.kho;
  const [ten, datTen] = useState(dong.tenThietBi ?? '');
  const [nguon, datNguon] = useState(dong.tenXa ? (dong.nguonId ?? '') : '');
  const [loi, datLoi] = useState<string | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const dsXa = cay.filter((n) => n.cap === 'xa');

  const gui = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datDangGui(true);
    datLoi(null);
    try {
      onXong(await api.sua(dong.id, { tenThietBi: ten, ...(nguon ? { nguonId: nguon } : {}) }));
    } catch (err) {
      datLoi(thongDiepLoi(err));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-che" role="presentation">
      <form
        className="hop-thoai ncc-bieu-mau"
        role="dialog"
        aria-modal="true"
        aria-label={t.suaTieuDe}
        onSubmit={(e) => void gui(e)}
      >
        <h2>{t.suaTieuDe}</h2>
        <p>
          <span className="tb-ma-chip">{dong.cumLoaId}</span>
        </p>
        <label>
          {t.suaTen}
          <input value={ten} onChange={(e) => datTen(e.target.value)} required maxLength={255} />
        </label>
        <label>
          {t.suaDiaBan}
          <select value={nguon} onChange={(e) => datNguon(e.target.value)}>
            <option value="">{dong.tenNguon ?? dong.nguonId ?? '—'}</option>
            {dsXa.map((n) => (
              <option key={n.maDinhDanh} value={n.maDinhDanh}>
                {n.ten}
              </option>
            ))}
          </select>
        </label>
        {loi ? (
          <p className="ncc-loi" role="alert">
            {loi}
          </p>
        ) : null}
        <div className="man-hinh__hanh-dong">
          <button type="button" className="nut" onClick={onHuy}>
            {t.huy}
          </button>
          <button type="submit" className="nut nut--chinh" disabled={dangGui}>
            {t.luu}
          </button>
        </div>
      </form>
    </div>
  );
}

type YeuCauKho =
  { kieu: 'dang-ky'; ids: string[] } | { kieu: 'dang-ky-tat-ca' } | { kieu: 'xoa'; ids: string[] } | null;

function BangKho({
  loai,
  cay,
  api,
  mocTai,
  dsNcc,
}: {
  loai: LoaiThietBiKho;
  cay: NutDiaBan[];
  api: KhoThietBiApi;
  mocTai: number;
  dsNcc: { maNhaCungCap: string; ten: string }[];
}): ReactNode {
  const t = viThietBi.quanTri;
  const tk = viThietBi.kho;
  // Nhà cung cấp gán cho thiết bị trực tiếp khi đăng ký; tỉnh chỉ có một nhà cung cấp thì chọn sẵn.
  const [ncc, datNcc] = useState('');
  const nccDungSan = dsNcc.length === 1 ? (dsNcc[0]?.maNhaCungCap ?? '') : '';
  const nccGui = ncc || nccDungSan;
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  const [nguon, datNguon] = useState('');
  const [oTim, datOTim] = useState('');
  const tuKhoa = dungHoan(oTim, TRE_TIM_KIEM_MS).trim();
  const [daChon, datDaChon] = useState<Set<string>>(new Set());
  const [yeuCau, datYeuCau] = useState<YeuCauKho>(null);
  const [dangSua, datDangSua] = useState<DongKho | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const [thongBao, datThongBao] = useState<ThongBao>(null);
  const [mocNoiBo, datMocNoiBo] = useState(0);

  const kq = dungTaiDuLieu<TrangDuLieu<DongKho>>({
    tai: (signal) =>
      api.danhSach(
        {
          trangThai: 'trong_kho',
          loaiThietBi: loai,
          nguonId: nguon || undefined,
          keyword: tuKhoa.length >= 2 ? tuKhoa : undefined,
          page: trang,
          pageSize: soDong,
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc: nguon !== '' || tuKhoa.length >= 2,
    phuThuoc: [loai, nguon, tuKhoa, trang, soDong, mocTai, mocNoiBo],
  });
  const dong = kq.duLieu?.items ?? [];
  const tong = kq.duLieu?.total ?? 0;
  const batDau = (trang - 1) * soDong;
  const tenXaChon = cay.find((n) => n.maDinhDanh === nguon)?.ten;
  // Đăng ký chỉ bật khi đã chọn đúng một XÃ; thiết bị được chọn phải báo đúng xã đó.
  const coXa = cay.some((n) => n.maDinhDanh === nguon && n.cap === 'xa');
  const dsChonKhopXa = dong
    .filter((d) => daChon.has(d.id))
    .every((d) => d.nguonId === nguon || laChuaCoXa(d));
  // Thiết bị trực tiếp tự kích hoạt qua DV018 không báo nhà cung cấp: phải chọn trước khi đăng ký.
  const thieuNcc =
    !nccGui && dong.some((d) => daChon.has(d.id) && d.phuongThucKetNoi === 'truc_tiep' && !d.maNhaCungCap);

  const taiLai = (): void => {
    datDaChon(new Set());
    datMocNoiBo((x) => x + 1);
  };

  const baoKetQua = (r: KetQuaDangKy): void => {
    datThongBao({
      loai: r.loi.length === 0 ? 'ok' : 'loi',
      chu:
        dienVao(tk.daDangKy, { ok: r.thanhCong.length, tong: r.tong }) +
        (r.loi.length ? ` ${dienVao(tk.dangKyLoi, { so: r.loi.length })}` : ''),
      chiTiet: [
        ...r.loi.map((x) => `${x.cumLoaId}: ${x.thongBao}`),
        // Thiết bị trực tiếp đã đăng ký nhưng chưa hoàn tất DV018: nhắc cách kích hoạt.
        ...r.thanhCong.filter((x) => x.canKichHoat).map((x) => dienVao(tk.canKichHoat, { ma: x.cumLoaId })),
      ],
    });
  };

  const thucHien = async (yc: NonNullable<YeuCauKho>): Promise<void> => {
    datDangGui(true);
    datThongBao(null);
    try {
      if (yc.kieu === 'xoa') {
        const r = await api.xoaKhoiKho(yc.ids);
        datThongBao({ loai: 'ok', chu: dienVao(tk.daXoaKho, { so: r.soDong }) });
      } else if (yc.kieu === 'dang-ky') {
        baoKetQua(
          await api.dangKy({
            ids: yc.ids,
            nguonId: nguon,
            loaiThietBi: loai,
            ...(nccGui ? { maNhaCungCap: nccGui } : {}),
          }),
        );
      } else {
        baoKetQua(
          await api.dangKy({
            tatCa: true,
            nguonId: nguon,
            loaiThietBi: loai,
            ...(nccGui ? { maNhaCungCap: nccGui } : {}),
          }),
        );
      }
    } catch (e) {
      datThongBao({ loai: 'loi', chu: thongDiepLoi(e) });
    } finally {
      datDangGui(false);
      taiLai();
    }
  };

  const diaBanCau = dienVao(tk.dangKyTaiDiaBan, { ten: tenXaChon ?? nguon });

  const cot: CotBang<DongKho>[] = [
    { khoa: 'stt', nhan: t.cot.stt, ve: (d) => batDau + dong.indexOf(d) + 1 },
    { khoa: 'diaDiem', nhan: t.cot.diaDiem, ve: (d) => <ODiaBan d={d} /> },
    { khoa: 'tenThietBi', nhan: t.cot.tenThietBi, ve: (d) => d.tenThietBi ?? '—' },
    { khoa: 'maThietBi', nhan: t.cot.maThietBi, ve: (d) => d.cumLoaId },
    {
      khoa: 'maNhaCungCap',
      nhan: tk.cot.maNhaCungCap,
      ve: (d) => d.maNhaCungCap,
    },
    { khoa: 'phuongThuc', nhan: tk.cot.phuongThuc, ve: (d) => <OPhuongThuc d={d} /> },
    { khoa: 'capNhat', nhan: tk.cot.capNhat, ve: (d) => thoiDiemNgan(d.lanCuoiThay) },
    {
      khoa: 'thaoTac',
      nhan: t.cot.thaoTac,
      ve: (d) => (
        <span className="bang__hanh-dong">
          <button
            type="button"
            className="nut nut--nho nut--nguy-hiem"
            disabled={dangGui}
            onClick={() => datYeuCau({ kieu: 'xoa', ids: [d.id] })}
          >
            {t.xoa}
          </button>
          <button type="button" className="nut nut--nho nut--chinh" onClick={() => datDangSua(d)}>
            {t.chinhSua}
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="thanh-loc-mau" data-vung="thanh-loc">
        <ChonDonViHanhChinh
          id="kho-loc-don-vi"
          giaTri={nguon}
          cay={cay}
          onDoi={(v) => {
            datNguon(v);
            datTrang(1);
            datDaChon(new Set());
          }}
        />
        <TruongLoc nhan={tk.nhanNccTrucTiep} htmlFor="kho-ncc">
          <select id="kho-ncc" value={nccGui} onChange={(e) => datNcc(e.target.value)}>
            <option value="">{tk.nccTheoThietBi}</option>
            {dsNcc.map((n) => (
              <option key={n.maNhaCungCap} value={n.maNhaCungCap}>
                {n.ten} ({n.maNhaCungCap})
              </option>
            ))}
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.nhanTimTheoTen} htmlFor="kho-tim-ten">
          <input
            id="kho-tim-ten"
            type="search"
            placeholder={t.timTheoTen}
            value={oTim}
            onChange={(e) => {
              datOTim(e.target.value);
              datTrang(1);
              datDaChon(new Set());
            }}
          />
        </TruongLoc>
      </div>

      <div className="tb-hang qt-hang-nut" data-vung="hang-nut-hang-loat">
        <span className="tb-nut-kem-nhan">
          <button type="button" className="nut" disabled>
            <BieuTuongNho duong={NET_DONG_BO} />
            {t.kichHoatThayThe}
          </button>
          <NhanChuaCoApi />
        </span>
        <button
          type="button"
          className="nut nut--chinh"
          data-hanh-dong="dang-ky-da-chon"
          disabled={!coXa || daChon.size === 0 || !dsChonKhopXa || thieuNcc || dangGui}
          title={!coXa ? tk.canChonXa : thieuNcc ? tk.canChonNcc : undefined}
          onClick={() => datYeuCau({ kieu: 'dang-ky', ids: [...daChon] })}
        >
          <BieuTuongNho duong={NET_TEP} />
          {t.dangKyDaChon}
          {daChon.size > 0 ? ` (${daChon.size})` : ''}
        </button>
        <button
          type="button"
          className="nut nut--chinh"
          data-hanh-dong="dang-ky-tat-ca"
          disabled={!coXa || tong === 0 || dangGui}
          title={coXa ? undefined : tk.canChonXa}
          onClick={() => datYeuCau({ kieu: 'dang-ky-tat-ca' })}
        >
          <BieuTuongNho duong={NET_TEP} />
          {t.dangKyTatCa}
        </button>
        <button
          type="button"
          className="nut nut--nguy-hiem"
          data-hanh-dong="xoa-khoi-kho"
          disabled={daChon.size === 0 || dangGui}
          onClick={() => datYeuCau({ kieu: 'xoa', ids: [...daChon] })}
        >
          <BieuTuongNho duong={NET_THUNG_RAC} />
          {t.xoaDaChon}
          {daChon.size > 0 ? ` (${daChon.size})` : ''}
        </button>
      </div>

      {!coXa && tong > 0 ? (
        <p className="qt-goi-y" role="note" data-vung="can-chon-xa">
          {tk.canChonXa}
        </p>
      ) : null}

      <DongThongBao tb={thongBao} />

      <BangDuLieu<DongKho>
        nhan={t.tieuDe}
        cot={cot}
        dong={dong}
        khoaDong={(d) => d.id}
        nhanDong={(d) => `${d.tenThietBi ?? d.cumLoaId} (${d.cumLoaId})`}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={tong}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={(so) => {
          datTrang(so);
          datDaChon(new Set());
        }}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
          datDaChon(new Set());
        }}
        daChon={daChon}
        onDoiChon={datDaChon}
        dangLoc={nguon !== '' || tuKhoa.length >= 2}
        onXoaLoc={() => {
          datNguon('');
          datOTim('');
          datTrang(1);
        }}
        moTaRong={tk.rongKho}
      />

      {yeuCau ? (
        <HopXacNhan
          tieuDe={yeuCau.kieu === 'xoa' ? tk.xoaKhoTieuDe : tk.dangKyDaChonTieuDe}
          heQua={
            yeuCau.kieu === 'xoa'
              ? dienVao(tk.xoaKhoHeQua, { so: yeuCau.ids.length })
              : yeuCau.kieu === 'dang-ky'
                ? dienVao(tk.dangKyHeQua, { so: yeuCau.ids.length, diaBan: diaBanCau })
                : dienVao(tk.dangKyTatCaHeQua, { so: tong, diaBan: diaBanCau })
          }
          nhanThucHien={yeuCau.kieu === 'xoa' ? t.xoa : tk.dangKy}
          onHuy={() => datYeuCau(null)}
          onThucHien={() => {
            const yc = yeuCau;
            datYeuCau(null);
            void thucHien(yc);
          }}
        />
      ) : null}

      {dangSua ? (
        <HopSuaKho
          dong={dangSua}
          cay={cay}
          api={api}
          onHuy={() => datDangSua(null)}
          onXong={(d) => {
            datDangSua(null);
            datThongBao({ loai: 'ok', chu: dienVao(tk.daSua, { ma: d.cumLoaId }) });
            taiLai();
          }}
        />
      ) : null}
    </>
  );
}

/* =========================== Đã đăng ký vào hệ thống ========================= */

type YeuCauXoa = { ids: string[]; ten: string };

function BangDaDangKy({
  loai,
  cay,
  goi,
}: {
  loai: LoaiThietBiKho;
  cay: NutDiaBan[];
  goi: NonNullable<QuanTriThietBiProps['api']>;
}): ReactNode {
  const phien = dungPhien();
  const t = viThietBi.quanTri;

  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  const [nguonLoc, datNguonLoc] = useState('');
  const [oTim, datOTim] = useState('');
  const tuKhoa = dungHoan(oTim, TRE_TIM_KIEM_MS).trim();
  const [daChon, datDaChon] = useState<Set<string>>(new Set());
  const [yeuCauXoa, datYeuCauXoa] = useState<YeuCauXoa | null>(null);
  const [dangXoa, datDangXoa] = useState(false);
  const [thongBao, datThongBao] = useState<ThongBao>(null);

  const nguonId = nguonLoc || phien.nguonIdHienTai || '';
  const dangLoc = nguonLoc !== '' || tuKhoa.length >= 2;

  const kq = dungTaiDuLieu<TrangDuLieu<DongThietBi>>({
    tai: (signal) =>
      goi.danhSach(
        {
          page: trang,
          pageSize: soDong,
          loaiThietBi: loai,
          nguonId: nguonId || undefined,
          // Từ khoá dưới hai ký tự không gửi lên (Bảng 3.1).
          keyword: tuKhoa.length >= 2 ? tuKhoa : undefined,
          sapXep: 'tenThietBi',
          huongSapXep: 'asc',
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [loai, trang, soDong, nguonId, tuKhoa],
  });

  const coQuyenXoa = phien.coQuyen('device.delete');
  const coQuyenSua = phien.coQuyen('device.update');

  const dong = kq.duLieu?.items ?? [];
  const batDau = (trang - 1) * soDong;
  const dsDaChon = dong.filter((d) => daChon.has(d.id));

  /** Xoá lần lượt từng thiết bị; lỗi của từng thiết bị được gom và báo lại. */
  const thucHienXoa = async (ids: string[]): Promise<void> => {
    datDangXoa(true);
    datThongBao(null);
    const loi: string[] = [];
    let soXoa = 0;
    for (const id of ids) {
      try {
        await goi.xoa(id);
        soXoa += 1;
      } catch (e) {
        loi.push(thongDiepLoi(e));
      }
    }
    datDangXoa(false);
    datDaChon(new Set());
    datThongBao(
      loi.length === 0
        ? { loai: 'ok', chu: dienVao(t.daXoa, { so: soXoa }) }
        : { loai: 'loi', chu: dienVao(t.xoaLoi, { so: loi.length, loi: [...new Set(loi)].join('; ') }) },
    );
    kq.taiLai();
  };

  const cot: CotBang<DongThietBi>[] = [
    { khoa: 'stt', nhan: t.cot.stt, ve: (d) => batDau + dong.indexOf(d) + 1 },
    {
      khoa: 'diaDiem',
      nhan: t.cot.diaDiem,
      ve: (d) => (
        <>
          {d.tenThon ?? d.maThon ?? '—'}
          <span className="bang__dong-phu">{d.tenXa ?? d.nguonId}</span>
        </>
      ),
    },
    { khoa: 'tenThietBi', nhan: t.cot.tenThietBi, ve: (d) => d.tenThietBi ?? '—' },
    { khoa: 'maThietBi', nhan: t.cot.maThietBi, ve: (d) => d.cumLoaId },
    {
      khoa: 'ngayCapNhat',
      nhan: t.cot.ngayCapNhat,
      ve: (d) => (d.capNhatLuc === null ? '—' : gioThanhChu(d.capNhatLuc)),
    },
    {
      khoa: 'thaoTac',
      nhan: t.cot.thaoTac,
      ve: (d) => (
        <span className="bang__hanh-dong">
          <button
            type="button"
            className="nut nut--nho nut--nguy-hiem"
            disabled={!coQuyenXoa || dangXoa}
            onClick={() => datYeuCauXoa({ ids: [d.id], ten: d.tenThietBi ?? d.cumLoaId })}
          >
            {t.xoa}
          </button>
          {coQuyenSua ? (
            <Link className="nut nut--nho nut--chinh" to={`/thiet-bi/${d.id}/sua`}>
              {t.chinhSua}
            </Link>
          ) : (
            <button type="button" className="nut nut--nho" disabled>
              {t.chinhSua}
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="thanh-loc-mau" data-vung="thanh-loc">
        <ChonDonViHanhChinh
          id="qt-loc-don-vi"
          giaTri={nguonLoc}
          cay={cay}
          onDoi={(v) => {
            datNguonLoc(v);
            datTrang(1);
            datDaChon(new Set());
          }}
        />
        <TruongLoc nhan={t.nhanTimTheoTen} htmlFor="qt-tim-ten">
          <input
            id="qt-tim-ten"
            type="search"
            placeholder={t.timTheoTen}
            value={oTim}
            onChange={(e) => {
              datOTim(e.target.value);
              datTrang(1);
              datDaChon(new Set());
            }}
          />
        </TruongLoc>
      </div>

      <div className="tb-hang qt-hang-nut" data-vung="hang-nut-hang-loat">
        <button
          type="button"
          className="nut nut--nguy-hiem"
          data-hanh-dong="xoa-da-chon"
          disabled={!coQuyenXoa || dsDaChon.length === 0 || dangXoa}
          onClick={() => datYeuCauXoa({ ids: dsDaChon.map((d) => d.id), ten: '' })}
        >
          <BieuTuongNho duong={NET_THUNG_RAC} />
          {t.xoaDaChon}
          {dsDaChon.length > 0 ? ` (${dsDaChon.length})` : ''}
        </button>
      </div>

      <DongThongBao tb={thongBao} />

      <BangDuLieu<DongThietBi>
        nhan={t.tieuDeDaDangKy}
        cot={cot}
        dong={dong}
        khoaDong={(d) => d.id}
        nhanDong={(d) => `${d.tenThietBi ?? d.cumLoaId} (${d.cumLoaId})`}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={(so) => {
          datTrang(so);
          datDaChon(new Set());
        }}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
          datDaChon(new Set());
        }}
        daChon={daChon}
        onDoiChon={datDaChon}
        dangLoc={dangLoc}
        onXoaLoc={() => {
          datNguonLoc('');
          datOTim('');
          datTrang(1);
        }}
      />

      {yeuCauXoa ? (
        <HopXacNhan
          tieuDe={t.xoaTieuDe}
          heQua={
            yeuCauXoa.ids.length === 1 && yeuCauXoa.ten
              ? dienVao(t.xoaHeQua, { so: 1, ten: yeuCauXoa.ten })
              : dienVao(t.xoaNhieuHeQua, { so: yeuCauXoa.ids.length })
          }
          nhanThucHien={t.xoa}
          onHuy={() => datYeuCauXoa(null)}
          onThucHien={() => {
            const ids = yeuCauXoa.ids;
            datYeuCauXoa(null);
            void thucHienXoa(ids);
          }}
        />
      ) : null}
    </>
  );
}

/* ================================== Trang =================================== */

export function QuanTriThietBi({ loai, api, apiKho }: QuanTriThietBiProps): ReactNode {
  const goi = api ?? thietBiApi;
  const kho = apiKho ?? khoThietBiApi;
  const phien = dungPhien();
  const t = viThietBi.quanTri;
  const [tab, datTab] = useState<'kho' | 'da-dang-ky'>('kho');
  const [moHop, datMoHop] = useState(false);
  const [mocKho, datMocKho] = useState(0);
  const [thongBao, datThongBao] = useState<ThongBao>(null);
  const coQuyenTao = phien.coQuyen('device.create');

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => (goi.cayDiaBan ? goi.cayDiaBan({}, signal) : Promise.resolve([])),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const dsNcc = dungTaiDuLieu<{ maNhaCungCap: string; ten: string }[]>({
    tai: (signal) => (goi.nhaCungCap ? goi.nhaCungCap(signal) : Promise.resolve([])),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const tieuDeTrang = loai === 'cum_thu_phat_thanh' ? vi.menuCon.quanTriCumLoa : vi.menuCon.quanTriBangDienTu;

  return (
    <section className="man-hinh" data-man-hinh="quan-tri-thiet-bi" data-loai={loai}>
      <nav className="vet-duong-dan" aria-label={vi.dieuHuong.quanTri}>
        <span>{vi.dieuHuong.quanTri}</span> / <span>{tieuDeTrang}</span>
      </nav>
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
        <div className="man-hinh__hanh-dong">
          {coQuyenTao ? (
            <button
              type="button"
              className="nut nut--chinh"
              data-hanh-dong="them-moi"
              onClick={() => datMoHop(true)}
            >
              <BieuTuongNho duong={NET_CONG} />
              {t.themMoi}
            </button>
          ) : null}
        </div>
      </header>

      <div className="tab" role="tablist">
        {(
          [
            ['kho', t.tabKho],
            ['da-dang-ky', t.tabDaDangKy],
          ] as const
        ).map(([k, nhan]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`tab__nut${tab === k ? ' tab__nut--dang-chon' : ''}`}
            onClick={() => datTab(k)}
          >
            {nhan}
          </button>
        ))}
      </div>

      <DongThongBao tb={thongBao} />

      {tab === 'kho' ? (
        <BangKho loai={loai} cay={cay.duLieu ?? []} api={kho} mocTai={mocKho} dsNcc={dsNcc.duLieu ?? []} />
      ) : (
        <BangDaDangKy loai={loai} cay={cay.duLieu ?? []} goi={goi} />
      )}

      {moHop ? (
        <HopThietBiKetNoi
          loai={loai}
          cay={cay.duLieu ?? []}
          api={kho}
          onDong={() => datMoHop(false)}
          onDaDuaVaoKho={(so) => {
            datMoHop(false);
            datTab('kho');
            datMocKho((x) => x + 1);
            datThongBao({ loai: 'ok', chu: dienVao(viThietBi.kho.daDuaVaoKho, { so }) });
          }}
        />
      ) : null}
    </section>
  );
}
